import { useState } from 'react';
import { formatCurrency, formatPercent } from '../lib/formatCurrency.js';

const ITEMS_PER_PAGE = 50;

const statusStyles = {
  Loss: 'border-rose-200 bg-rose-50 text-rose-700',
  Thin: 'border-amber-200 bg-amber-50 text-amber-700',
  Safe: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  Good: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  Invalid: 'border-slate-200 bg-slate-100 text-slate-600',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${statusStyles[status]}`}>
      {status}
    </span>
  );
}

function EmptyValue() {
  return <span className="text-slate-400">-</span>;
}

export default function ProductTable({ products, onLoadProduct }) {
  const [currentPage, setCurrentPage] = useState(1);

  if (products.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
        Belum ada produk yang diimport.
      </div>
    );
  }

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedProducts = products.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <div className="overflow-x-auto">
        <table className="min-w-[1080px] divide-y divide-slate-200 text-left text-sm">
          <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-4 py-3">SKU</th>
              <th className="px-4 py-3">Produk</th>
              <th className="px-4 py-3">Marketplace</th>
              <th className="px-4 py-3">Kategori</th>
              <th className="px-4 py-3 text-right">Harga Jual</th>
              <th className="px-4 py-3 text-right">HPP</th>
              <th className="px-4 py-3 text-right">Net Payout</th>
              <th className="px-4 py-3 text-right">Net Profit</th>
              <th className="px-4 py-3 text-right">Margin</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {paginatedProducts.map((product) => {
              const result = product.result;
              const status = product.isValid ? result?.profitStatus || 'Thin' : 'Invalid';

              return (
                <tr key={`${product.rowNumber}-${product.input.sku}`} className="align-top hover:bg-slate-50">
                  <td className="px-4 py-4 font-semibold text-slate-800">
                    {product.input.sku || <EmptyValue />}
                    <p className="mt-1 text-xs font-normal text-slate-400">Baris {product.rowNumber}</p>
                  </td>
                  <td className="px-4 py-4">
                    <div className="font-medium text-slate-900">{product.input.productName || <EmptyValue />}</div>
                    {product.warnings.length > 0 && (
                      <div className="mt-2 space-y-1">
                        {product.warnings.map((warning, index) => (
                          <p key={`${warning}-${index}`} className="max-w-xs text-xs leading-5 text-amber-700">
                            {warning}
                          </p>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-4 text-slate-600">{result?.marketplaceLabel || product.input.marketplace || '-'}</td>
                  <td className="px-4 py-4 text-slate-600">{result?.categoryLabel || product.input.category || '-'}</td>
                  <td className="px-4 py-4 text-right font-semibold text-slate-800">
                    {formatCurrency(product.input.sellingPrice)}
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-slate-800">
                    {formatCurrency(product.input.costOfGoods)}
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-slate-800">
                    {result ? formatCurrency(result.netPayout) : <EmptyValue />}
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-slate-800">
                    {result ? formatCurrency(result.netProfit) : <EmptyValue />}
                  </td>
                  <td className="px-4 py-4 text-right font-semibold text-slate-800">
                    {result ? formatPercent(result.marginPercentage) : <EmptyValue />}
                  </td>
                  <td className="px-4 py-4">
                    <StatusBadge status={status} />
                  </td>
                  <td className="px-4 py-4 text-right">
                    <button
                      type="button"
                      disabled={!product.isValid}
                      onClick={() => onLoadProduct(product.input)}
                      className="rounded-lg border border-emerald-600 px-3 py-2 text-xs font-semibold text-emerald-700 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-transparent"
                    >
                      Muat
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-slate-200 bg-slate-50 px-4 py-3">
          <div className="text-sm text-slate-600">
            Menampilkan {startIndex + 1}-{Math.min(endIndex, products.length)} dari {products.length} produk
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-slate-50"
            >
              Sebelumnya
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    type="button"
                    onClick={() => goToPage(pageNum)}
                    className={`rounded-lg px-3 py-2 text-sm font-semibold transition ${
                      currentPage === pageNum
                        ? 'bg-emerald-700 text-white'
                        : 'border border-slate-300 text-slate-700 hover:border-slate-400 hover:bg-white'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-400 hover:bg-white disabled:cursor-not-allowed disabled:border-slate-200 disabled:text-slate-400 disabled:hover:bg-slate-50"
            >
              Berikutnya
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
